/**
 * Common navigation blocker + confirmation dialog logic
 * Reusable across different form change guard hooks
 */

import React from 'react';
import { useBlocker } from 'react-router-dom';
import { FORM_MESSAGES } from '@/constants/formMessages';

// Module-level variable to track if navigation is authorized (to prevent double confirmation)
let isNavigationAuthorized = false;
let authorizeNavigationResetTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Helper hook: Common navigation blocker + confirmation dialog logic
 * Extracted to avoid duplication between useFormChangeGuard and useMassagerPageFormGuard
 *
 * Uses the browser-native window.confirm() dialog when navigation is blocked
 * (e.g. browser back button), so the user sees the browser's own UI instead of
 * the app's custom modal.
 */
export const useFormConfirmationBlocker = (hasFormChanged: boolean, onClearChange: () => void) => {
  const blockedStateCounterRef = React.useRef(0);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Don't block if navigation is already authorized
    if (isNavigationAuthorized) {
      isNavigationAuthorized = false; // Reset for next navigation
      return false;
    }

    const shouldBlock = hasFormChanged && currentLocation.pathname !== nextLocation.pathname;
    return shouldBlock;
  });

  React.useEffect(() => {
    if (blocker.state !== 'blocked') {
      blockedStateCounterRef.current = 0;
      return;
    }

    // Only show confirmation once per blocked state
    blockedStateCounterRef.current += 1;
    const currentCounter = blockedStateCounterRef.current;

    if (currentCounter > 1) {
      return;
    }

    // Use the browser's native confirm dialog so the user sees the browser's
    // own UI (content and button labels determined by the browser).
    const shouldLeave = window.confirm(FORM_MESSAGES.DISCARD_CHANGES_CONFIRMATION);

    if (shouldLeave) {
      try {
        blocker.proceed();
      } catch (error) {
        blockedStateCounterRef.current = 0;
      }
      onClearChange();
    } else {
      blockedStateCounterRef.current = 0;
      blocker.reset();
    }
  }, [blocker.state, blocker, onClearChange]);
};

/**
 * Mark the next navigation as authorized (skip blocker confirmation)
 */
export const authorizeNavigation = () => {
  isNavigationAuthorized = true;
  if (authorizeNavigationResetTimer) {
    clearTimeout(authorizeNavigationResetTimer);
  }
  // Keep authorization alive long enough for async/scheduled navigation
  // (e.g. delete success flow), but auto-reset if navigation never happens.
  authorizeNavigationResetTimer = setTimeout(() => {
    isNavigationAuthorized = false;
    authorizeNavigationResetTimer = null;
  }, 2000);
};
